import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import TableHeader from '../../../../components/TableHeader/TableHeader'
import PageLoading from '../../../../components/PageLoading/PageLoading'
import FechaSelector from '../../../../components/form/FechaSelector/FechaSelector'
import { useNotification } from '../../../../context/NotificationManager/NotificationManager'
import {
  dateToString,
  getToday,
  dateToDateTime,
  dateTimeToString
} from '../../../../utils/date'
import { getLlamadas } from '../../../../utils/api/llamadas'
import { textFilter } from '../../../../utils/table'
import { formatDuration } from '../../../../utils/model/llamadas'
import {
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import ModalTranscripcionLlamada from '../../../../modals/ModalTranscripcionLlamada/ModalTranscripcionLlamada'
import { getCSVContent, downloadCSVFile } from '../../../../utils/csv'
import css from './Llamadas.module.css'

const Llamadas = () => {
  const notification = useNotification()

  const [fecha, setFecha] = useState(dateToDateTime(getToday()))
  const [search, setSearch] = useState('')
  const [expandedCalls, setExpandedCalls] = useState({})
  const [llamadaSeleccionada, setLlamadaSeleccionada] = useState(null)

  const { isLoading, data: llamadas = [] } = useQuery({
    queryKey: ['marketing', 'llamadas', 'list', 'day', fecha],
    queryFn: () =>
      getLlamadas(dateToString(fecha)).catch(err => {
        notification.error({
          title: 'Error al recuperar los datos de llamadas',
          content: err
        })
        return []
      })
  })

  const toggleExpand = id => {
    setExpandedCalls(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const filterSearch = llamada => {
    const camposBusqueda = {
      telefono: llamada.telefono,
      nombre: llamada.usuario?.nombre,
      centro: llamada.usuario?.centro
    }
    return textFilter({
      object: camposBusqueda,
      fields: ['telefono', 'nombre', 'centro'],
      search: search
    })
  }

  const handleExport = () => {
    const content = getCSVContent({
      columns: ['Fecha', 'Tipo', 'Usuario', 'Centro', 'Teléfono', 'Duración'],
      data: llamadas.filter(filterSearch),
      transform: c => [
        dateTimeToString(c.fechaInicio) || '--',
        c.tipoLlamada?.replace('LLAMADA_', '') || '--',
        c.usuario?.nombre || '--',
        c.usuario?.centro || '--',
        c.telefono || '--',
        formatDuration(c.duracionLlamada) || '--'
      ]
    })
    downloadCSVFile(content, `llamadas - ${dateToString(fecha)}`)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
            <FechaSelector value={fecha} onChange={setFecha} />
          </div>
        }
        title='Llamadas'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
      />
      <div className={css.main}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Centro</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell align='center'>Duración</TableCell>
                <TableCell align='center'>Acciones</TableCell>
                <TableCell align='center'></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {llamadas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align='center'>
                    No hay llamadas
                  </TableCell>
                </TableRow>
              )}
              {llamadas.filter(filterSearch).map(llamada => (
                <Fragment key={llamada.id}>
                  <TableRow className={css.tableRow} hover>
                    <TableCell>
                      {dateTimeToString(llamada.fechaInicio) || '--'}
                    </TableCell>
                    <TableCell>
                      {llamada.tipoLlamada?.replace('LLAMADA_', '') || '--'}
                    </TableCell>
                    <TableCell>{llamada.usuario?.nombre || '--'}</TableCell>
                    <TableCell>{llamada.usuario?.centro || '--'}</TableCell>
                    <TableCell>{llamada.telefono || '--'}</TableCell>
                    <TableCell align='center'>
                      {formatDuration(llamada.duracionLlamada)}
                    </TableCell>
                    <TableCell align='center'>
                      <div className={css.action}>
                        <IconButton
                          color='primary'
                          size='small'
                          onClick={() => setLlamadaSeleccionada(llamada)}
                          title='Ver transcripción completa'
                        >
                          <i className='material-icons'>description</i>
                        </IconButton>
                      </div>
                    </TableCell>
                    <TableCell align='center'>
                      <IconButton
                        size='small'
                        onClick={() => toggleExpand(llamada.id)}
                        title='Ver resumen'
                      >
                        <i className='material-icons'>
                          {expandedCalls[llamada.id]
                            ? 'keyboard_arrow_up'
                            : 'keyboard_arrow_down'}
                        </i>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={css.collapse} colSpan={7}>
                      <Collapse
                        in={!!expandedCalls[llamada.id]}
                        timeout='auto'
                        unmountOnExit
                      >
                        <div className={css.resumen}>
                          <p className={css.text}>
                            {llamada.resumen ||
                              'No hay resumen disponible para esta llamada'}
                          </p>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <ModalTranscripcionLlamada
          open={!!llamadaSeleccionada}
          onClose={() => setLlamadaSeleccionada(null)}
          llamada={llamadaSeleccionada}
        />
      </div>
    </PageLoading>
  )
}

export default Llamadas
